import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import { noResult } from "../images";
import { debounce } from "../helper";
import SearchModal, { SearchModalProps } from "./Modals/SearchModal";

type Header = {
  key: string;
  label: string;
  hideInMobile?: boolean;
  format?: (value: any, allValue: any) => string | ReactElement;
  align?: "start" | "center" | "end";
};

type DataViewProps = {
  data: Record<string, any>[];
  headers: Header[];
  loading?: boolean;
  noResultImage?: string;
  loadMoreData?: (perPage?: number) => void;
  handleViewButtonClick?: (data: any) => void;
  onPerPageChange?: (perPage: number) => void;
  searchComponentProps?: SearchModalProps;
};

const TableGrid: React.FC<DataViewProps> = ({
  data,
  headers,
  loading = false,
  loadMoreData,
  handleViewButtonClick,
  onPerPageChange,
  searchComponentProps,
}) => {
  const [perPage, setPerPage] = useState(20);
  const handleScroll = useCallback(() => {
    if (
      document.body.scrollHeight - 300 <
      window.scrollY + window.innerHeight
    ) {
      loadMoreData(perPage || 20);
    }
  }, [loadMoreData, perPage]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 100);
    if (loadMoreData) {
      window.addEventListener("scroll", debouncedHandleScroll);
    }
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [handleScroll, loadMoreData]);

  return (
    <>
      <div
        className={`py-2 gap-3 flex ${
          searchComponentProps && searchComponentProps.searchFields.length > 0
            ? "justify-between"
            : "justify-end"
        } items-center`}
      >
        {searchComponentProps &&
          searchComponentProps.searchFields.length > 0 && (
            <SearchModal {...searchComponentProps} perPage={perPage} />
          )}
        <div className="flex items-center gap-3 ">
          <p className="text-sm text-gray-600 sm:block hidden">
            Records per page
          </p>
          <div className="relative w-20">
            <select
              className={`w-full appearance-none border border-gray-300 p-2 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-800 placeholder-gray-500 invalid:border-1  focus:outline-none focus:shadow-none focus:ring-0 focus:border-gray-900 text-sm bg-white`}
              onChange={(e) => {
                onPerPageChange(parseInt(e.target.value));
                setPerPage(parseInt(e.target.value));
              }}
            >
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
            <IconChevronDown
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              size={20}
            />
          </div>
        </div>
      </div>
      <div className="overflow-hidden rounded-[10px] border border-gray-300 bg-white max-[900px]:hidden ">
        <div className="overflow-auto">
          {data?.length === 0 && loading ? (
            <div className="flex items-center justify-center w-full h-[70vh]">
              <LuLoaderCircle className="animate-spin h-9 w-9 text-indigo-700" />
            </div>
          ) : data?.length > 0 ? (
            <table className="w-full min-w-[1000px]">
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header.key}
                      className={`px-3 py-4 bg-zinc-500 text-sm text-white text-${
                        header.align || "start"
                      } font-normal`}
                    >
                      {header.label}
                    </th>
                  ))}
                  {handleViewButtonClick && (
                    <th
                      key="See All Details"
                      className="px-3 py-4 bg-zinc-500 text-sm text-white text-start font-normal"
                    >
                      See All Details
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index}>
                    {headers.map((header) => (
                      <td
                        key={header.key}
                        className="px-3 py-3 text-sm text-gray-600 border-t border-gray-200"
                      >
                        {header.format
                          ? header.format(item[header.key], item)
                          : item[header.key] || "N/A"}
                      </td>
                    ))}
                    {handleViewButtonClick && (
                      <td className="px-3 py-4 text-sm text-blue-500 border-t border-gray-200">
                        <div
                          className="inline-flex items-center justify-center gap-1 cursor-pointer "
                          onClick={() => handleViewButtonClick(item)}
                          role="presentation"
                        >
                          <span>View</span>
                          <IconInfoCircle size={20} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                <tr></tr>
                <tr>
                  <td colSpan={headers.length + 1} className="text-center">
                    {loading && (
                      <LuLoaderCircle className="animate-spin inline-block h-9 w-9 text-indigo-700 my-2" />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="text-center p-6 bg-white rounded-xl">
              <img src={noResult} alt="No Results" className="mx-auto" />
              <p className="text-gray-400">No items found</p>
            </div>
          )}
        </div>
      </div>
      <div className="max-[900px]:block hidden">
        {data?.length === 0 && loading ? (
          <div className="flex items-center justify-center w-full h-[70vh]">
            <LuLoaderCircle className="animate-spin h-9 w-9 text-indigo-700" />
          </div>
        ) : data?.length > 0 ? (
          <div className=" mb-10 ">
            <div className="gap-4 min-[740px]:grid-cols-2 grid">
              {data.map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl overflow-hidden border border-gray-300 bg-white text-sm"
                >
                  <div className="px-3 py-3 bg-zinc-500 text-white text-start font-normal flex items-center justify-between">
                    <h3>{item.contractNumber || item.id || "N/A"}</h3>
                    {handleViewButtonClick && (
                      <div
                        className="text-primary inline-flex items-center justify-center gap-1 cursor-pointer"
                        onClick={() => handleViewButtonClick(item)}
                      >
                        <span>View</span>
                        <IconInfoCircle size={20} />
                      </div>
                    )}
                  </div>
                  <div className="p-3 grid gap-y-2.5">
                    {headers
                      .filter(({ hideInMobile }) => !hideInMobile)
                      .map((header) => (
                        <div
                          className="flex items-center [&:nth-child(2)]:order-1"
                          key={header.key}
                        >
                          <p className="min-w-[130px] text-gray-900">
                            {header.label}
                          </p>

                          {header.format ? (
                            header.format(item[header.key], item)
                          ) : (
                            <p className="text-gray-500">
                              {item[header.key] || "N/A"}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
            {loading && (
              <div className="text-center pt-2">
                <LuLoaderCircle className="animate-spin inline-block h-9 w-9 text-indigo-700" />
              </div>
            )}
          </div>
        ) : !loading ? (
          <div className="text-center p-6 bg-white rounded-xl">
            <img src={noResult} alt="No Results" className="mx-auto" />
            <p className="text-gray-400">No items found</p>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default TableGrid;
